import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "@/context/UserContext";
import api from "@/lib/api";
import { InquiryForm } from "@/components/InquiryForm";
import { BookOpenCheck, ChevronRight, LogOut } from "lucide-react";

const statusColor = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-amber-100 text-amber-800",
  enrolled: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-500",
};

export const Dashboard = () => {
  const { user, logout } = useUser();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/inquiries").then((r) => setInquiries(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <span className="eyebrow">My Dashboard</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-semibold text-[#0A3B32] tracking-tight">
            Welcome back, {user?.name?.split(" ")[0] || "Student"}.
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Track your enquiries and manage your enrolment in one place.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {user?.picture && (
            <img
              src={user.picture}
              alt=""
              className="w-10 h-10 rounded-full border border-slate-200"
            />
          )}
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="dashboard-logout"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-5">
          <h2 className="font-heading text-2xl font-semibold text-[#0A3B32]">Your enquiries</h2>
          {loading ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : inquiries.length === 0 ? (
            <div className="card-soft" data-testid="dashboard-empty">
              <BookOpenCheck className="text-[#C85A40]" />
              <p className="mt-3 font-heading text-lg font-semibold text-[#0A3B32]">
                No enquiries yet
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Ready to root your career? Reserve your seat for the flagship 45-day course.
              </p>
              <Link
                to="/courses/medical-devices-15-day"
                className="btn-primary mt-5"
                data-testid="dashboard-enroll-cta"
              >
                Reserve a Free Seat <ChevronRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {inquiries.map((i) => (
                <div key={i.id} className="card-soft" data-testid={`dashboard-inquiry-${i.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-heading font-semibold text-[#0A3B32]">
                        {i.course_slug || "General enquiry"}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(i.created_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                        statusColor[i.status] || "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {i.status}
                    </span>
                  </div>
                  {i.message && (
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {i.message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="card-soft" data-testid="dashboard-quick-enroll">
            <p className="eyebrow">Quick enquiry</p>
            <h3 className="font-heading mt-2 text-xl font-semibold text-[#0A3B32]">
              Reserve a Free Seat
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Our counsellor will call you within 24 hours with fees and batch details.
            </p>
            <div className="mt-4">
              <InquiryForm compact />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};
